import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { ButtonType, OptionsHelper, showAlert, showError } from '_metronic/helpers'
import {
  Button,
  FetchState,
  FormInput,
  FormSelect,
  FormTextArea,
  FormToggle,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
} from 'app/components'
import { ExameDtoCadastro, ExameView } from '../../core/_models'
import { exameSchema } from './validation/exameSchema'
import { createExame, updateExame } from '../../core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'

export const ExameModal: FC = () => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: exame } = (fetchStates['exame'] || {}) as FetchState<ExameView>

  const initialValues = {
    nome: exame?.nome || '',
    codigo: exame?.codigo || '',
    tipo: exame?.tipo || '',
    descricao: exame?.descricao || '',
    ativo: exame
      ? { label: exame.ativo ? 'Ativo' : 'Inativo', value: exame.ativo }
      : (undefined as IOption<boolean> | undefined),
    apac: exame?.apac || false,
  }

  const formik = useFormik({
    initialValues,
    validationSchema: useMemo(() => exameSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const data: ExameDtoCadastro = {
          nome: values.nome,
          codigo: values.codigo || undefined,
          tipo: values.tipo || undefined,
          descricao: values.descricao || undefined,
          ativo: values.ativo?.value as boolean,
          apac: values.apac,
        }

        if (exame?.id) {
          await updateExame(exame.id, data)
        } else {
          await createExame(data)
        }

        await showAlert({
          title: t('common.success'),
          text: exame?.id
            ? t('alert.update', { name: t('exame.label'), gender: t('exame.gender') })
            : t('alert.create', { name: t('exame.label'), gender: t('exame.gender') }),
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className='row'>
        <div className='col-md-8 mb-3'>
          <FormInput formik={formik} name='nome' entity='nome' isRequired />
        </div>
        <div className='col-md-4 mb-3'>
          <FormInput
            formik={formik}
            name='codigo'
            entity='codigo'
            isRequired={formik.values.apac}
          />
        </div>
        <div className='col-md-6 mb-3'>
          <FormInput formik={formik} name='tipo' entity='tipo' isRequired={formik.values.apac} />
        </div>
        <div className='col-md-6 mb-3'>
          <FormSelect
            formik={formik}
            name='ativo'
            entity='situacao'
            options={OptionsHelper.statusCadastro}
            isClearable={false}
            isDisabled={formik.isSubmitting}
          />
        </div>
        <div className='col-md-12 mb-3'>
          <FormTextArea formik={formik} name='descricao' entity='descricao' />
        </div>
      </div>

      <div className='d-flex flex-wrap gap-4 mb-3'>
        <FormToggle formik={formik} name='apac' entity='apac' />
      </div>

      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
